import { type ReactElement, useLayoutEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import type { PageGraphSource } from './canvasTypes'
import { FlowAnimationLayer } from './FlowAnimationLayer'
import { setFlowScale } from './flowScale'
import { projectNodeOverlays } from './graphGeometry'
import { GraphNodeOverlayLayer } from './GraphNodeOverlayLayer'
import { usePageFlowAnimation } from './usePageFlowAnimation'
import { usePageIsolation } from './usePageIsolation'

const noActiveNodeIds: string[] = []

const OverlaySvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
  && * {
    pointer-events: none;
    cursor: inherit;
  }
`

export function PreviewGraphOverlay({
  pageGraphs,
  imageNatural,
  pxPerImagePx,
}: {
  pageGraphs: PageGraphSource[]
  imageNatural: { w: number; h: number }
  pxPerImagePx: number
}): ReactElement | null {
  const svgRef = useRef<SVGSVGElement>(null)
  const overlays = useMemo(
    () =>
      projectNodeOverlays(
        pageGraphs.map(({ graph, pageNumber }) => ({
          graph,
          pageNumber,
          bounds: {
            denormalized_x: 0,
            denormalized_y: 0,
            denormalized_width: imageNatural.w,
            denormalized_height: imageNatural.h,
          },
        }))
      ),
    [pageGraphs, imageNatural.w, imageNatural.h]
  )
  const { stops, highlights, isolating } = usePageIsolation({
    pageGraphs,
    isActiveTab: true,
  })
  const flowLayer = usePageFlowAnimation({
    pageGraphs,
    overlays,
    isActiveTab: true,
    stops,
    highlights,
    isolating,
  })
  const visible = flowLayer !== undefined
  useLayoutEffect(() => {
    if (visible) setFlowScale(svgRef.current, pxPerImagePx)
  }, [visible, pxPerImagePx])
  if (!flowLayer) return null
  return (
    <OverlaySvg
      ref={svgRef}
      viewBox={`0 0 ${imageNatural.w} ${imageNatural.h}`}
      preserveAspectRatio='none'
      aria-hidden
      data-preview-graph>
      <GraphNodeOverlayLayer
        overlays={overlays}
        activeNodeIds={noActiveNodeIds}
      />
      <FlowAnimationLayer {...flowLayer} />
    </OverlaySvg>
  )
}
